/**
 * GC-W9 — single source of truth for the golden "named callout" layer
 * (caveat / assumption boxes) that surfaces gating diligence conditions on a
 * report section.
 *
 * WHY THIS MODULE EXISTS: the callouts must appear identically on EVERY
 * surface — the React view, the print/PDF deck (both share the React
 * renderer) AND the hand-written DOCX / PPTX serializers. Encoding the
 * gate + body inline in each section component would force the serializers
 * to re-derive the same conditions, and the two copies WOULD drift (exactly
 * the hand-written-serializer divergence the golden audit flagged). Instead
 * the gate + grounded body live here ONCE; the section components and the
 * DOCX/PPTX exporters all consume `resolveSectionCallout`.
 *
 * NO-FABRICATION CONTRACT: every callout is gated on a REAL `report.*` field
 * and its body quotes verbatim values only. A clean / absent field returns
 * `null` (nothing renders). Never invent a number, date, or status here.
 */
import type { SiteRagReport } from '../../types/siteRag';
import { resolveSupplyChainPlan } from './supplyChainPlan';
import {
  computeCriticalPathResolved,
  projectPhasesForCriticalPath,
} from '../criticalPathAnalysis';

/** Golden filled tones — the only two named (filled) callout treatments. */
export type ReportCalloutTone = 'caveat' | 'assumption';

export interface ReportCallout {
  /** Canonical section id (matches reportSections.ts + the DOCX/PPTX dispatch). */
  sectionId: string;
  tone: ReportCalloutTone;
  label: string;
  body: string;
}

/** Round to 1 decimal — mirrors CapacityCreditDeepDiveSection's r1(). */
function r1(mw: number): number {
  return Math.round(mw * 10) / 10;
}

/** Finite-number guard — mirrors PublicSentiment/index.tsx finiteNumber(). */
function finiteNumber(n: number | undefined): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

/**
 * Resolve the single golden callout (if any) for a section. Returns `null`
 * when the section's real data does not meet the caveat/assumption condition.
 *
 * Keep this switch in lock-step with the section components — they render
 * EXACTLY what this returns (see each section's `resolveSectionCallout` call).
 */
export function resolveSectionCallout(
  report: SiteRagReport,
  sectionId: string,
): ReportCallout | null {
  switch (sectionId) {
    /* ── Environmental ─────────────────────────────────────────────── */
    case 'airQualityEmissions': {
      const status = report.airQualityEmissions?.attainmentStatus;
      const fires =
        typeof status === 'string' &&
        /non[- ]?attainment|nonattainment|maintenance|moderate|serious|severe|extreme/i.test(status);
      if (!fires) return null;
      return {
        sectionId,
        tone: 'caveat',
        label: 'Air Permitting',
        body: `The site falls within a ${status} area. On-site generation above major-source thresholds may trigger NNSR/PSD air-permitting – a gating schedule item to confirm with the state air agency before final design.`,
      };
    }

    /* ── Water resource ────────────────────────────────────────────── */
    case 'waterRights': {
      const withdrawalPermit = report.waterAnalysis?.consumptiveUse?.withdrawalPermit;
      const fires =
        typeof withdrawalPermit === 'string' &&
        /unconfirmed|pending|required|tbd|outstanding|not (confirmed|obtained|secured)/i.test(withdrawalPermit);
      if (!fires) return null;
      return {
        sectionId,
        tone: 'assumption',
        label: 'Assumption · To be confirmed',
        body: `The water-withdrawal permit is recorded as "${withdrawalPermit}" and is not yet secured. This report assumes the withdrawal right is obtainable; the permit must be confirmed and secured before final design.`,
      };
    }

    case 'droughtResilience': {
      if (report.datasets?.climate?.waterStress !== 'High') return null;
      return {
        sectionId,
        tone: 'caveat',
        label: 'Planning Caveat',
        body: 'The site sits in a High WRI baseline water-stress band. High baseline water stress may constrain water-withdrawal permitting and favour dry or hybrid cooling – confirm allocation headroom with the water authority before committing to a wet-cooled design.',
      };
    }

    /* ── Power availability ────────────────────────────────────────── */
    case 'interconnectionQueue': {
      const queueTime = report.datasets?.grid?.estimatedQueueTime;
      if (!(typeof queueTime === 'string' && queueTime.trim().length > 0)) return null;
      return {
        sectionId,
        tone: 'caveat',
        label: 'Grid Queue',
        body: report.datasets?.grid?.estimatedQueueTimeBasis === 'site-load-service'
          ? `The site load-service estimate is ${queueTime}. Confirm its scope and current status with the utility before adopting a power-availability date.`
          : 'Site load-service timing is unconfirmed. Regional generation-queue benchmarks and proximity heuristics do not establish this project’s service date; obtain the applicable utility study.',
      };
    }

    case 'gridCapacity': {
      const gridCapacity = report.datasets?.grid?.capacity;
      if (!(gridCapacity === 'Limited' || gridCapacity === 'Constrained')) return null;
      return {
        sectionId,
        tone: 'caveat',
        label: 'Hosting Capacity',
        body: `The grid dataset carries a ${gridCapacity.toLowerCase()} screening label. That label and nearby infrastructure do not establish available hosting capacity or a project shortfall; confirm headroom with an applicable utility system-impact study.`,
      };
    }

    case 'capacityCreditDeepDive': {
      const firmVerdict = report.firmCapacityVerdict;
      if (firmVerdict?.firmDeliverableMW == null) return null;
      const capacity = report.generationImpactProjection?.capacityAndResilience;
      if (capacity?.capacityRole === 'grid-supply') {
        return {
          sectionId,
          tone: 'caveat',
          label: 'Planning Caveat',
          body: `The ${r1(firmVerdict.firmDeliverableMW)} MW figure is aggregate default capacity credit, not firm site supply. `
            + `The selected grid-feed scenario models ${r1(capacity.contingencyNetMW)} MW surviving ${capacity.criterion}, `
            + `against ${r1(capacity.facilityLoadMW)} MW facility load and ${r1(capacity.reserveRequiredMW)} MW reserve-padded requirement. `
            + (capacity.qualificationNote ?? 'Utility service and shared common-cause independence remain unqualified.'),
        };
      }
      const phaseComp = firmVerdict.phaseComparison;
      const reserveClause =
        typeof firmVerdict.reservePct === 'number'
          ? phaseComp?.requiredFirmMW != null && phaseComp?.requiredMW != null
            ? ` is evaluated against ${r1(phaseComp.requiredFirmMW)} MW required firm capacity (${r1(phaseComp.requiredMW)} MW load plus a ${firmVerdict.reservePct}% NERC-style planning reserve margin${phaseComp.phaseLabel ? ` for ${phaseComp.phaseLabel}` : ''}) under`
            : ` is evaluated against a ${firmVerdict.reservePct}% NERC-style planning reserve margin under`
          : ' reflects';
      return {
        sectionId,
        tone: 'caveat',
        label: 'Planning Caveat',
        body: `The worst-case firm-deliverable supply figure of ${r1(firmVerdict.firmDeliverableMW)} MW (planning-default ELCC accreditation)${reserveClause} a resource-adequacy planning default – an engineering posture, distinct from the ELCC value to be confirmed above. It is not a guaranteed deliverable and should be validated against the interconnecting balancing authority's accreditation methodology.`
          + (capacity?.qualificationNote ? ` ${capacity.qualificationNote}` : ''),
      };
    }

    /* ── Feasibility ───────────────────────────────────────────────── */
    case 'gridStudy': {
      const gridDataset = report.datasets?.grid;
      const gridQueueTime = gridDataset?.estimatedQueueTime?.trim();
      const gridCapacityConstrained =
        gridDataset?.capacity === 'Limited' || gridDataset?.capacity === 'Constrained';
      if (!(Boolean(gridQueueTime) || gridCapacityConstrained)) return null;
      const iso = gridDataset?.isoRegion ?? 'interconnecting';
      return {
        sectionId,
        tone: 'caveat',
        label: 'Grid Queue',
        body: gridQueueTime && gridDataset?.estimatedQueueTimeBasis === 'site-load-service'
          ? `The site load-service estimate is ${gridQueueTime}; confirm its scope and hosting-capacity headroom with the ${iso} ISO/utility before adopting the schedule.`
          : `Site load-service duration and available headroom remain unconfirmed. Regional benchmarks, proximity heuristics and screening capacity labels do not establish either; obtain the applicable ${iso} utility study.`,
      };
    }

    /* ── Supply-chain risk ─────────────────────────────────────────── */
    case 'supplierRiskRanking': {
      const plan = resolveSupplyChainPlan(report);
      const soleSourceNames = (plan?.bom ?? [])
        .filter((item) => item.criticality === 'critical-path' && item.substitutability === 'sole-source')
        .map((item) => item.name);
      if (soleSourceNames.length === 0) return null;
      return {
        sectionId,
        tone: 'assumption',
        label: 'Assumption · Single-source supplier',
        body:
          soleSourceNames.length === 1
            ? `${soleSourceNames[0]} is a critical-path line with no qualified substitute (sole-source). The schedule assumes this single vendor performs – confirm a second-source path or accept the single-vendor dependency.`
            : `${soleSourceNames.length} critical-path lines have no qualified substitute (sole-source): ${soleSourceNames.join(', ')}. The schedule assumes these single vendors perform – confirm second-source paths or accept the single-vendor dependencies.`,
      };
    }

    case 'criticalPathProcurement': {
      const plan = resolveSupplyChainPlan(report);
      const phases = projectPhasesForCriticalPath(report.projectIntentAtGen);
      const cod = report.projectIntentAtGen?.inServiceDate ?? null;
      const cp = plan ? computeCriticalPathResolved(plan, cod, phases) : null;
      const llcItems =
        report.supplyChainAnalysis?.longLeadVsCod?.items?.filter(
          (it) => !!it && (it.item || it.leadTimeMonths || it.orderByDate || it.slackVsCod || it.slipRisk),
        ) ?? [];
      const hasNegativeLlcSlack = llcItems.some((it) => it.slackVsCod?.trim().startsWith('-'));
      if (!(!!cp?.hasOverdueItems || hasNegativeLlcSlack)) return null;
      return {
        sectionId,
        tone: 'caveat',
        label: 'Planning Caveat · Long-Lead Procurement',
        body: 'The order-by dates below assume the planning COD holds. One or more long-lead items show negative slack against that COD – confirm those order placements and OEM slots before they become binding.',
      };
    }

    /* ── Financial ─────────────────────────────────────────────────── */
    case 'riskAdjustedWacc': {
      if (report.financialAssumptionsAtGen?.source !== 'industry-baseline') return null;
      return {
        sectionId,
        tone: 'assumption',
        label: 'Assumption · To be confirmed',
        body: 'WACC, LCOE and CAPEX rest on industry-baseline defaults pending a site-specific financial model – figures to confirm against an uploaded or manually entered model before final investment commitment.',
      };
    }

    /* ── Public sentiment (executive) ──────────────────────────────────
       NB: the public-sentiment executive is rendered inline in the report
       view (it has no registry sectionId), so this case keys on the synthetic
       id 'publicSentimentExecutive'. The DOCX/PPTX serializers iterate the
       registry section ids and will not request this id, so the coverage
       caveat is intentionally view/PDF-only. ----------------------------- */
    case 'publicSentimentExecutive': {
      const meta = report.publicSentimentMeta;
      const sourcesAttempted = meta?.sourcesAttempted;
      const fires =
        meta?.status === 'partial' ||
        meta?.status === 'unavailable' ||
        (finiteNumber(sourcesAttempted) && sourcesAttempted <= 1);
      if (!fires) return null;
      return {
        sectionId,
        tone: 'caveat',
        label: 'Planning Caveat · Data Coverage',
        body:
          meta?.status === 'unavailable'
            ? 'Sentiment-source coverage was unavailable for this site; the reception read below should be treated as a methodology placeholder, not a finding.'
            : finiteNumber(sourcesAttempted)
              ? `This sentiment read rests on ${sourcesAttempted} source${sourcesAttempted === 1 ? '' : 's'} of public-record coverage – thin coverage, so the read below is a directional planning signal rather than a settled finding.`
              : 'Public-sentiment source coverage for this site is partial; the read below is a directional planning signal rather than a settled finding.',
      };
    }

    default:
      return null;
  }
}

/**
 * All registry-keyed section callouts that fire for a report, in section
 * order. Used by the DOCX/PPTX serializers to mirror the view's callouts.
 * Excludes 'publicSentimentExecutive' (no registry section — view/PDF only).
 */
const REGISTRY_CALLOUT_SECTION_IDS: readonly string[] = [
  'airQualityEmissions',
  'waterRights',
  'droughtResilience',
  'interconnectionQueue',
  'gridCapacity',
  'capacityCreditDeepDive',
  'gridStudy',
  'supplierRiskRanking',
  'criticalPathProcurement',
  'riskAdjustedWacc',
];

export function resolveReportCallouts(report: SiteRagReport): ReportCallout[] {
  const out: ReportCallout[] = [];
  for (const id of REGISTRY_CALLOUT_SECTION_IDS) {
    const callout = resolveSectionCallout(report, id);
    if (callout) out.push(callout);
  }
  return out;
}
